import styled from "@emotion/styled"
import { PText } from "@porsche-design-system/components-react"
import { spacingStaticSm } from "@porsche-design-system/components-react/emotion"
import { forwardRef } from "react"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { Tooltip } from "../tooltip/Tooltip"
import { Stitching } from "./Stitching"

interface InteriorColorTileProps {
	className?: string // set by emotion when extended via styled(InteriorColorTile)
	cockpitColor?: string
	cockpitStitchingColor?: string
	label?: string
	seatColor: string
	seatStitchingColor?: string
	size?: "large"
	tooltip?: string
}

const clampWhite = (color: string) => (color === "#ffffff" ? "#efefef" : color)

export const InteriorColorTile = forwardRef<HTMLDivElement, InteriorColorTileProps>(
	(
		{ className, cockpitColor, cockpitStitchingColor, label, seatColor, seatStitchingColor = "", size, tooltip },
		ref,
	) => {
		const topColor = clampWhite(cockpitColor ?? seatColor)
		const bottomColor = clampWhite(seatColor)
		const topStitching = clampWhite(cockpitStitchingColor ?? seatStitchingColor)
		const bottomStitching = clampWhite(seatStitchingColor)

		return label ? (
			<Wrapper className={className} ref={ref}>
				<PreviewContainer className={!label ? className : ""} $size={size}>
					<Preview $color={topColor} />
					<Preview $color={bottomColor} />
					<Stitching topStitchingColor={topStitching} bottomStitchingColor={bottomStitching} />
				</PreviewContainer>
				<Tooltip content={tooltip}>
					<Label color="inherit" ellipsis>
						{label}
					</Label>
				</Tooltip>
			</Wrapper>
		) : (
			<PreviewContainer className={!label ? className : ""} $size={size} ref={ref}>
				<Preview $color={topColor} />
				<Preview $color={bottomColor} />
				<Stitching topStitchingColor={topStitching} bottomStitchingColor={bottomStitching} />
			</PreviewContainer>
		)
	},
)
InteriorColorTile.displayName = "InteriorColorTile"

const Wrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${spacingStaticSm};
`

const PreviewContainer = styled("div", { shouldForwardProp })<{ $size?: "large" }>`
	position: relative;
	overflow: hidden;
	display: flex;
	flex-grow: 0;
	flex-shrink: 0;
	flex-direction: column;
	border-radius: 4px;
	width: ${({ $size }) => ($size === "large" ? "36px" : "24px")};
	height: ${({ $size }) => ($size === "large" ? "36px" : "24px")};
`

const Preview = styled("span", { shouldForwardProp })<{ $color?: string }>`
	height: 50%;
	width: 100%;
	background: ${(props) => props.$color};
`

const Label = styled(PText)`
	min-width: 0;
`
